import React, { useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import { motion } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import NorthIcon from '@mui/icons-material/North';
import { faL } from "@fortawesome/free-solid-svg-icons";
import { getAllTestimonials } from "../../redux/network/genericApi";
import { setIsLoginDialog } from "../../redux/reducers/loginSlice";
import { useDispatch, useSelector } from "react-redux";
import { useInView } from "framer-motion";
import { faUsers, faCirclePlay, faCheckDouble } from "@fortawesome/free-solid-svg-icons";

import PreSignDialog from "../PreSign/PreSignDialog";
import ReactGA from 'react-ga4';
import { Users, PlayCircle, ShieldCheck, Smartphone, ArrowRight } from "lucide-react";
import AppDownloadBadges from "../Common/AppDownloadBadges";
import VisistProductBanner from "../Common/VisistProductBanner";
import HeroLaunchCapsule from "../Common/HeroLaunchCapsule";

const StatCounter = ({ value, label, icon: Icon, suffix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-100px" });

  useEffect(() => {
    if (inView) {
      let startTime;
      const duration = 2000;
      const endValue = parseInt(value.replace(/\D/g, ""));

      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = Math.min((timestamp - startTime) / duration, 1);
        
        // Power 4 Out easing
        const ease = 1 - Math.pow(1 - progress, 4);
        
        setCount(Math.floor(ease * endValue));

        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }
  }, [inView, value]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={inView ? { opacity: 1, scale: 1 } : {}}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
      className="group relative flex flex-col items-center p-8 md:p-12 bg-neutral-900/40 border border-white/10 rounded-[3rem] hover:bg-neutral-800/60 hover:border-emerald/30 transition-all duration-500 ease-out hover:-translate-y-2 hover:shadow-2xl will-change-transform"
    >
      {/* Decorative Glow (No Blur) */}
      <div className="absolute inset-x-0 bottom-0 h-2 bg-emerald/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-b-[3rem]"></div>
      
      {/* Icon Container */}
      <div className="w-20 h-20 bg-emerald/10 rounded-2xl flex items-center justify-center mb-10 border border-emerald/20 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 ease-out">
        <Icon className="w-10 h-10 text-emerald drop-shadow-[0_0_15px_rgba(16,185,129,0.5)]" strokeWidth={1.5} />
      </div>

      {/* Stats */}
      <div className="flex flex-col items-center">
        <h3 className="text-5xl md:text-6xl font-black text-white tracking-tight mb-4 flex items-baseline">
          <span className="bg-gradient-to-b from-white to-white/50 bg-clip-text text-transparent">
            {count.toLocaleString()}
          </span>
          <span className="text-emerald ml-1">{suffix}</span>
        </h3>
        <p className="text-gray-400 text-xs md:text-sm font-bold uppercase tracking-[0.3em] text-center leading-relaxed">
          {label}
        </p>
      </div>
    </motion.div>
  );
};

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);
  const testimonialsRef = useRef(null);
  const userRole = localStorage.getItem("BQS_ROLE");
  const token = localStorage.getItem("BQS_TOKEN");
  const userData = useSelector((state) => state.user.profile);
  const subscriptionsState = useSelector((state) => state.user.subscriptions);
  const activeSubs = subscriptionsState?.value || [];
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  // Check if user is authenticated and is a coach
  const isAuthenticated = !!token && userData?.value?.profileData;
  const isCoach = userRole === 'coach';

  // Subscription detection logic
  const enforcement = (process.env.REACT_APP_SUBSCRIPTION_ENFORCEMENT || 'hard').trim().toLowerCase();
  const isEnforced = enforcement === 'hard';
  const membershipProfile = userData?.value?.profileData?.membershipProfile;
  const hasActiveSubscription = !isEnforced || activeSubs.length > 0 || (membershipProfile && (membershipProfile.isActive === true || (membershipProfile.remainingReviews ?? 0) > 0));
  // console.log("testimonials===>", testimonials)

  const fetchTestimonials = async () => {
    try {
      const formData = {
        "condition": {
          "_id": ""
        }
      };

      const response = await getAllTestimonials(formData);

      // console.log("testimonials response", response);


      if (response.code === 200 && Array.isArray(response.data)) {
        setTestimonials(response.data);
      }

    } catch (error) {
      console.error(error.message);
    }
  };

  useEffect(() => {
    fetchTestimonials();
  }, []);

  // GA: page view for Home on route change
  useEffect(() => {
    try {
      ReactGA.send({ hitType: 'pageview', page: location.pathname || '/', title: 'Home' });
    } catch { }
  }, [location.pathname]);

  const [expanded, setExpanded] = useState({});

  const toggleReadMore = (index) => {
    setExpanded((prevState) => ({
      ...prevState,
      [index]: !prevState[index]
    }));
  };


  // Close expanded testimonial when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (testimonialsRef.current && !testimonialsRef.current.contains(event.target)) {
        setExpanded({}); // Reset expanded state to collapse all testimonials
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);


  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isArrowHover, setisArrowHover] = useState(false)

  // (removed earlier duplicate upload; see single upload handler below)

  const handleArrowButtonMouseEnter = () => {
    setisArrowHover(true);
  };

  const handleArrowButtonMouseLeave = () => {
    setisArrowHover(false);

  };

  const report = [
    {
      head: "Record & Upload ",
      sanp: "Your Videos",
      pera: "Record your training drills, practice matches, or competitive games using your smartphone. Upload the video to your Journal and follow the guidelines for accurate feedback and insights!",

      img: "assets/1st.jpeg.jpg",
      redirectionPath: userRole === "player"
        ? (hasActiveSubscription ? "/game-posts/game-videos" : "/subscription-pricing")
        : userRole === "coach"
          ? "/coach_flow/pending-video"
          : null
    },
    {
      head: "Request Your ",
      sanp: "Personalized Feedback",
      pera: "Upload your video and choose the area you want to improve. Our experts and Visual Intelligence Platform will create a custom feedback report just for you!",

      img: "assets/2nd.jpeg",
      redirectionPath: userRole === "player"
        ? (hasActiveSubscription ? "/game-posts/game-videos" : "/subscription-pricing")
        : userRole === "coach"
          ? "/coach_flow/pending-video"
          : null
    },
    {
      head: "Access All Your Videos and Reports ",
      sanp: "My Journal",
      pera: "Easily find all your uploaded videos and personalized AI feedback reports in your 'My Journal'—your one-stop space to track and improve your performance!",

      img: "assets/3rd.jpeg",
      redirectionPath: userRole === "player"
        ? (hasActiveSubscription ? "/game-posts/game-videos" : "/subscription-pricing")
        : userRole === "coach"
          ? "/coach_flow/pending-video"
          : null
    },
  ];
  /* Blog section removed as part of News page transformation */
  const blog = [];


  const partners = [
    {
      img: "assets/nvidia-inceptio-program-logo.jpg"
    },
    {
      img: "assets/ms-startups-founders-hub-logo.png"
    },
    {
      img: "assets/google-cloud-logo.png"
    },
    {
      img: "assets/fsid-iisc-logo-2.png"
    },
    {
      img: "assets/psb_logo.png"
    },
    {
      img: "assets/govtka_logo.png"
    },
  ];

  const sliderRef = useRef(null);

  const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    arrows: false,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        }
      },
      {
        breakpoint: 720,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
          initialSlide: 1
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 428,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1
        }
      }
    ]

  };



  const goToSlide = (index) => {
    if (sliderRef.current && index >= 0 && index < blog.length) {
      sliderRef.current.slickGoTo(index);
      setCurrentSlide(index);
    }
  };

  // const nextSlide = () => {
  //   if (sliderRef.current) {
  //     const nextIndex = (currentSlide + 1) % blog.length;
  //     sliderRef.current.slickGoTo(nextIndex);
  //     setCurrentSlide(nextIndex);
  //   }
  // };

  // const prevSlide = () => {
  //   if (sliderRef.current) {
  //     const prevIndex = (currentSlide - 1 + blog.length) % blog.length;
  //     sliderRef.current.slickGoTo(prevIndex);
  //     setCurrentSlide(prevIndex);
  //   }
  // };

  const renderPaginationNumbers = () => {
    const paginationNumbers = [];
    const totalSlides = blog.length;

    if (totalSlides <= 7) {
      // If there are 7 or fewer slides, render all pagination numbers
      for (let i = 0; i < totalSlides; i++) {
        paginationNumbers.push(i);
      }
    } else {
      // If there are more than 7 slides, render pagination numbers with ellipsis
      if (currentSlide <= 3) {
        // If current slide is within the first 4 slides
        for (let i = 0; i < 5; i++) {
          paginationNumbers.push(i);
        }
        paginationNumbers.push('...');
        paginationNumbers.push(totalSlides - 1);
      } else if (currentSlide >= totalSlides - 4) {
        // If current slide is within the last 4 slides
        paginationNumbers.push(0);
        paginationNumbers.push('...');
        for (let i = totalSlides - 5; i < totalSlides; i++) {
          paginationNumbers.push(i);
        }
      } else {
        // If current slide is in the middle
        paginationNumbers.push(0);
        paginationNumbers.push('...');
        for (let i = currentSlide - 1; i <= currentSlide + 1; i++) {
          paginationNumbers.push(i);
        }
        paginationNumbers.push('...');
        paginationNumbers.push(totalSlides - 1);
      }
    }

    return paginationNumbers.map((pageNumber, index) => (
      <span
        key={index}
        onClick={() => goToSlide(pageNumber)}
        style={{
          cursor: 'pointer',
          padding: '5px',
          margin: '0 5px',
          fontWeight: currentSlide === pageNumber ? 'bold' : 'normal',
        }}
      >
        {pageNumber === '...' ? '...' : pageNumber + 1}
      </span>
    ));
  };

  const reviews = [
    {
      name: "Srikar's Parent",
      content: 'I was pleasantly surprised by the nuances of techniques that were brought out by the analysis, and the corrections suggested. This has incredible potential for all badminton players who are keen on improving their game.',
    },
    {
      name: 'Jane Smith',
      content: 'I was pleasantly surprised by the nuances of techniques that were brought out by the analysis, and the corrections suggested. This has incredible potential for all badminton players who are keen on improving their game.',
    },
    {
      name: 'Jane Smith',
      content: 'I was pleasantly surprised by the nuances of techniques that were brought out by the analysis, and the corrections suggested. This has incredible potential for all badminton players who are keen on improving their game.',
    },
    {
      name: 'Jane Smith',
      content: 'I was pleasantly surprised by the nuances of techniques that were brought out by the analysis, and the corrections suggested. This has incredible potential for all badminton players who are keen on improving their game.',
    },
    {
      name: 'Jane Smith',
      content: 'I was pleasantly surprised by the nuances of techniques that were brought out by the analysis, and the corrections suggested. This has incredible potential for all badminton players who are keen on improving their game.',
    },
    {
      name: 'Jane Smith',
      content: 'I was pleasantly surprised by the nuances of techniques that were brought out by the analysis, and the corrections suggested. This has incredible potential for all badminton players who are keen on improving their game.',
    },
    {
      name: 'Jane Smith',
      content: 'I was pleasantly surprised by the nuances of techniques that were brought out by the analysis, and the corrections suggested. This has incredible potential for all badminton players who are keen on improving their game.',
    },
  ];

  // const experts = [
  //   { id: 1, name: "Harshit Aggarwal", image: "assets/expert1.png", ranking: "BWF World Ranking: 96", about: "Former India #1" },
  //   { id: 2, name: "Subhankar Dey", image: "assets/expert2.png", ranking: "BWF World Ranking: 37", about: "Former India #1" },
  //   { id: 3, name: "Luka", image: "assets/expert3.png", ranking: "BWF World Ranking: 52", about: "Former Austria #1" },
  //   { id: 4, name: "Siddharth Thakur", image: "assets/expert4.png", ranking: "BWF World Ranking: 120", about: "Former India #3" }
  // ]

  const review = {
    dots: false,
    infinite: true,
    slidesToShow: 4,
    slidesToScroll: 1,
    autoplay: true,
    speed: 2000,
    autoplaySpeed: 2000,
    cssEase: "linear",
    arrows: false,
    initialSlide: 0,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 3,
          slidesToScroll: 3,
        }
      },
      {
        breakpoint: 575,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
          initialSlide: 2
        }
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 428,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1
        }
      }
    ]
  };

  // const review = {
  //   dots: false,
  //   infinite: false,
  //   slidesToShow: 1,
  //   slidesToScroll: 1,
  //   autoplay: false,
  //   speed: 2000,
  //   autoplaySpeed: 2000,
  //   cssEase: "linear",
  //   arrows: false,
  //   initialSlide: 0,
  // };

  //Button functionality
  const [isCTAClicked, setIsCTAClicked] = useState(false)

  // Handle login redirect after authentication
  // Also clear the route state so it doesn't persist on back navigation
  useEffect(() => {
    if (location.state?.showLogin) {
      if (!isAuthenticated) {
        dispatch(setIsLoginDialog(true));
      }
      // Clear the state so the dialog doesn't pop again when navigating back
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state, isAuthenticated, dispatch, navigate, location.pathname]);
  const [showPreSign, setShowPreSign] = useState(false);

  // If the user returns from /signup via browser Back, show PreSign again
  useEffect(() => {
    try {
      const shouldOpen = sessionStorage.getItem('openPreSignOnReturn') === '1';
      if (shouldOpen) {
        setShowPreSign(true);
        sessionStorage.removeItem('openPreSignOnReturn');
        sessionStorage.removeItem('fromPreSignNav');
      }
    } catch { }
  }, []);

  // CTA button: open Pre-Sign dialog
  const upload = () => {
    setShowPreSign(true);
    window.scrollTo(0, 0);
    try { ReactGA.event({ category: 'Home', action: 'cta_explore_click' }); } catch { }
  }
  const nextSectionRef = useRef(null);

  const scrollToNextSection = () => {
    nextSectionRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const handleClickGetReport = () => {
    if (userRole === "player") {
      if (hasActiveSubscription) {
        navigate('/game-posts/game-videos');
      } else {
        navigate('/subscription-pricing');
      }
    } else if (userRole === "coach") {
      navigate('/coach_flow/pending-video');
    } else {
      setShowPreSign(true);
      window.scrollTo(0, 0);
    }
    try { ReactGA.event({ category: 'Home', action: 'cta_get_report_click', label: userRole || 'guest' }); } catch { }
  }

  const handleClickBog = (index) => {
    try { ReactGA.event({ category: 'Home', action: 'blog_card_click', label: String(index + 1) }); } catch { }
    navigate(`/blogdetails/${index + 1}`)
  }


  //Back Button functionality

  // useEffect(() => {
  //   const handlePopstate = (event) => {
  //     const r = window.confirm("You pressed a Back button! Are you sure?!");
  //     if (r) {
  //       navigate("/");
  //     } else {
  //       // Restore the URL to prevent the user from leaving the page
  //       window.history.pushState(null, null, window.location.pathname);
  //     }
  //   };

  //   window.addEventListener("popstate", handlePopstate);

  //   return () => {
  //     window.removeEventListener("popstate", handlePopstate);
  //   };
  // }, [navigate]);



  return (
    <div className="bg-white">
      {/* Hero Section - Full Viewport with Dark Background */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-deep-charcoal">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video autoPlay loop muted playsInline preload="metadata" className="w-full h-full object-cover">
            <source src="/banner.mp4" type="video/mp4" />
          </video>
          {/* Very Mild Black Overlay */}
          <div className="absolute inset-0 bg-black/20"></div>
          {/* Lighter Dark Overlays - Video More Visible */}
          <div className="absolute inset-0 bg-gradient-to-b from-deep-charcoal/10 via-deep-charcoal/15 to-deep-charcoal/30"></div>


        </div>

        {/* Content */}
        <div className="relative z-10 container mx-auto px-6 text-center">
          {/* Hero Launch Capsule HUD */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8 inline-block"
          >
            <HeroLaunchCapsule appName="all" />
          </motion.div>

          {/* Main Headline with Framer Motion */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black text-white uppercase tracking-tight leading-tight mb-6"
          >
            Where AI agents build <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">better athletes</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="text-light-gray text-lg md:text-xl lg:text-2xl max-w-4xl mx-auto mb-12 leading-relaxed"
            style={{
              textShadow: '0 2px 8px rgba(0, 0, 0, 0.9), 0 4px 16px rgba(0, 0, 0, 0.7)'
            }}
          >
            Our <span className="text-white font-bold" style={{ textShadow: 'none' }}>Visual Intelligence Platform<sup className="text-xs">TM</sup></span> turns training and match videos into instant, actionable insights for players, coaches, and academies across sports starting with badminton...
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4 -mt-4 md:mt-0"
          >
            {!isAuthenticated && (
              <button
                onClick={() => navigate('/products')}
                className="group relative px-6 py-3 md:px-8 md:py-4 bg-gradient-to-r from-emerald to-emerald/80 text-white font-bold text-base md:text-lg rounded-lg border border-white hover:shadow-glow-blue transition-all duration-300 hover:scale-105 overflow-hidden"
              >
                <span className="relative z-10">EXPLORE PRODUCTS</span>
                <div className="absolute inset-0 bg-gradient-to-r from-emerald/0 via-white/20 to-emerald/0 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700"></div>
              </button>
            )}
            {isAuthenticated && (
              <button
                onClick={() => {
                  if (userRole === 'player') {
                    if (hasActiveSubscription) {
                      navigate('/game-posts');
                    } else {
                      navigate('/subscription-pricing');
                    }
                  } else {
                    navigate('/coach_flow');
                  }
                }}
                className="px-6 py-3 md:px-8 md:py-4 bg-emerald text-white font-bold text-base md:text-lg rounded-lg hover:bg-emerald/80 hover:shadow-glow-blue transition-all duration-300 hover:scale-105"
              >
                {userRole === 'player' ? 'MY SMARTJOURNAL' : 'MY REVIEWS'}
              </button>
            )}
          </motion.div>

          {/* PreSign Dialog */}
          <PreSignDialog
            open={showPreSign}
            onClose={() => setShowPreSign(false)}
            onSuccess={() => {
              setShowPreSign(false);
              try {
                sessionStorage.setItem('openPreSignOnReturn', '1');
                sessionStorage.setItem('fromPreSignNav', '1');
              } catch { }
              navigate('/signup');
            }}
          />
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 cursor-pointer"
          onClick={scrollToNextSection}
        >
          <div className="flex flex-col items-center gap-2 text-white/60 hover:text-white transition-colors duration-300">
            <span className="text-xs uppercase tracking-widest font-medium">Scroll</span>
            <motion.div
              animate={{ y: [0, 8, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 5V19M12 19L5 12M12 19L19 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* Impact Achievement Section */}
      <section className="relative py-24 md:py-32 bg-[#0a0a0a] overflow-hidden border-b border-white/5">
        {/* Abstract Background Highlights (No Blur) */}
        <div className="absolute inset-0 opacity-5 pointer-events-none">
          <div className="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 bg-gradient-to-br from-emerald/20 to-transparent rounded-full"></div>
          <div className="absolute -bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-gradient-to-tl from-emerald/10 to-transparent rounded-full"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16 md:mb-20"
          >
            <p className="text-emerald text-xs md:text-sm font-bold uppercase tracking-[0.4em] mb-4">Impact</p>
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-black text-white uppercase tracking-tight">
              Our Outstanding <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">Achievements</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            <StatCounter 
              value="2000" 
              suffix="+" 
              label="Students Onboarded" 
              icon={Users} 
            />
            <StatCounter 
              value="15000" 
              suffix="+" 
              label="User Videos Processed" 
              icon={PlayCircle} 
            />
            <StatCounter 
              value="98" 
              suffix="%" 
              label="AI Analysis Accuracy" 
              icon={ShieldCheck} 
            />
          </div>
        </div>
      </section>

      {/* Mobile Apps Launch Announcement Section */}
      <section id="mobile-apps" className="relative py-24 bg-[#050505] overflow-hidden border-b border-white/10">
        {/* Glowing Background Orbs */}
        <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-96 h-96 bg-emerald/10 rounded-full blur-[100px] pointer-events-none"></div>
        <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-96 h-96 bg-purple-500/10 rounded-full blur-[100px] pointer-events-none"></div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald/10 border border-emerald/30 text-emerald text-xs md:text-sm font-bold uppercase tracking-widest mb-6">
              <Smartphone className="w-4 h-4" />
              NOW ON MOBILE • APP STORE & PLAY STORE
            </div>
            <h2 className="text-4xl md:text-6xl font-black text-white uppercase tracking-tight mb-6">
              Two Apps. <br className="hidden sm:inline" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald via-neon-lime to-emerald">
                Unlimited Athletic Intelligence.
              </span>
            </h2>
            <p className="text-gray-300 text-lg md:text-xl font-light leading-relaxed max-w-2xl mx-auto">
              We are thrilled to launch the official <span className="text-white font-bold">SmartAssessment</span> and <span className="text-white font-bold">SmartJournal</span> mobile apps on both iOS and Android!
            </p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 max-w-6xl mx-auto">
            {/* SmartAssessment Card */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="group relative bg-[#0a0a0a] border border-white/10 hover:border-emerald/40 rounded-[2.5rem] overflow-hidden shadow-2xl transition-all duration-500 hover:-translate-y-2 flex flex-col justify-between"
            >
              <VisistProductBanner productName="SmartAssessment" height="h-64" />

              <div className="p-8 pt-6 flex-1 flex flex-col justify-between space-y-6">
                <div>
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="px-3 py-1 rounded-full bg-emerald/20 text-emerald text-xs font-bold uppercase tracking-wider border border-emerald/30">
                          For Academies & Coaches
                        </span>
                      </div>
                      <h3 className="text-2xl md:text-3xl font-black text-white leading-tight">SmartAssessment</h3>
                    </div>
                    <img
                      src="/assets/SmartAssessment%20Logo.png"
                      alt="SmartAssessment Logo"
                      className="w-20 h-20 md:w-24 md:h-24 object-contain drop-shadow-xl shrink-0"
                    />
                  </div>
                  <p className="text-gray-300 text-sm md:text-base leading-relaxed">
                    Automate skill evaluation, benchmark footwork & technique consistency across training groups, and share visual reports directly with players and parents.
                  </p>
                </div>
                <div className="space-y-4 pt-2">
                  <AppDownloadBadges appName="SmartAssessment" theme="glass" size="md" showTitle titleText="Download SmartAssessment" />
                  <div>
                    <button
                      onClick={() => navigate('/products/smart-assessments')}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-emerald hover:text-emerald-400 transition-colors"
                    >
                      Learn more about SmartAssessment
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* SmartJournal Card */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="group relative bg-[#0a0a0a] border border-white/10 hover:border-purple-500/40 rounded-[2.5rem] overflow-hidden shadow-2xl transition-all duration-500 hover:-translate-y-2 flex flex-col justify-between"
            >
              <VisistProductBanner productName="SmartJournal" height="h-64" />

              <div className="p-8 pt-6 flex-1 flex flex-col justify-between space-y-6">
                <div>
                  <div className="flex items-start justify-between gap-4 mb-4">
                    <div>
                      <div className="flex items-center gap-2 mb-3">
                        <span className="px-3 py-1 rounded-full bg-purple-500/20 text-purple-300 text-xs font-bold uppercase tracking-wider border border-purple-500/30">
                          For Athletes
                        </span>
                      </div>
                      <h3 className="text-2xl md:text-3xl font-black text-white leading-tight">SmartJournal</h3>
                    </div>
                    <img
                      src="/assets/SmartJournal%20Logo.png"
                      alt="SmartJournal Logo"
                      className="w-20 h-20 md:w-24 md:h-24 object-contain drop-shadow-xl shrink-0"
                    />
                  </div>
                  <p className="text-gray-300 text-sm md:text-base leading-relaxed">
                    Your personal video logbook. Capture training sessions on the go, track long-term progress trends, and interact with VISIST AI for instant feedback.
                  </p>
                </div>
                <div className="space-y-4 pt-2">
                  <AppDownloadBadges appName="SmartJournal" theme="glass" size="md" showTitle titleText="Download SmartJournal" />
                  <div>
                    <button
                      onClick={() => navigate('/products/smart-journals')}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-purple-400 hover:text-purple-300 transition-colors"
                    >
                      Learn more about SmartJournal
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Personalized Report Section */}
      <section className="relative py-24 bg-white overflow-hidden" ref={nextSectionRef}>
        {/* Background Accent */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-96 bg-gray-50"></div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 uppercase mb-4">
              Get Your Personalized Video Report
            </h2>
            <p className="text-emerald text-2xl md:text-3xl font-bold">in 3 Simple Steps</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {report.map((ele, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.2 }}
                className="group relative bg-white border border-gray-200 rounded-2xl overflow-hidden hover:border-emerald hover:shadow-xl transition-all duration-300 hover:-translate-y-2"
              >
                <div className="relative h-64 overflow-hidden">
                  <img src={ele.img} alt={ele.head} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                </div>
                <div className="p-6">

                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {ele.head} <span className="text-emerald">{ele.sanp}</span>
                  </h3>
                  {ele.scnSnap && <p className="text-gray-600 text-sm mb-3">{ele.scnSnap}</p>}
                  <p className="text-gray-600 mb-6 leading-relaxed">{ele.pera}</p>
                  {ele.btn && (
                    <button
                      onClick={() => {
                        if (ele.redirectionPath) {
                          navigate(ele.redirectionPath);
                          window.scrollTo(0, 0);
                        } else {
                          setShowPreSign(true);
                        }
                      }}
                      className="w-full px-6 py-3 bg-gradient-to-r from-emerald to-emerald/80 text-white font-bold rounded-full hover:shadow-glow-blue transition-all duration-300 hover:scale-105"
                    >
                      {ele.btn}
                    </button>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center mt-12"
          >
            <button
              onClick={() => {
                if (userRole === "player") {
                  if (hasActiveSubscription) {
                    navigate("/game-posts/game-videos");
                  } else {
                    navigate("/subscription-pricing");
                  }
                  window.scrollTo(0, 0);
                } else if (userRole === "coach") {
                  navigate("/coach_flow/pending-video");
                  window.scrollTo(0, 0);
                } else {
                  setShowPreSign(true);
                }
              }}
              className="px-12 py-4 bg-gradient-to-r from-emerald to-emerald/80 text-white font-bold text-lg rounded-lg hover:shadow-glow-blue transition-all duration-300 hover:scale-105"
            >
              Explore Now
            </button>
          </motion.div>
        </div>
      </section>
      {/* Value Pillars Section */}
      <section className="relative py-24 bg-gradient-to-b from-white via-gray-50 to-white overflow-hidden">
        {/* Decorative Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 left-10 w-72 h-72 bg-emerald/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-neon-lime/5 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-emerald text-sm md:text-base font-bold uppercase tracking-widest mb-3">Tailored Solutions</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 uppercase mb-4">
              Built For <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">Everyone</span>
            </h2>
            <p className="text-gray-600 text-lg md:text-xl max-w-3xl mx-auto">
              Empowering players, coaches, and academies with AI-driven insights
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-10">
            {/* Players Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="group relative bg-white border-2 border-gray-200 rounded-3xl p-8 hover:border-emerald transition-all duration-500 hover:shadow-2xl hover:-translate-y-3 overflow-hidden"
            >
              {/* Gradient Accent */}
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-emerald to-emerald/60 rounded-t-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              {/* Icon Container */}
              <div className="relative mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald/10 to-emerald/5 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                  <svg className="w-10 h-10 text-emerald" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                {/* Decorative dot */}
                <div className="absolute -top-2 -right-2 w-4 h-4 bg-emerald rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>

              {/* Content */}
              <h3 className="text-2xl md:text-3xl font-black text-gray-900 mb-4 group-hover:text-emerald transition-colors duration-300">
                Players
              </h3>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                Elevate skills & match insights with AI-powered video breakdowns.
              </p>

              {/* Hover Effect Line */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-emerald to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </motion.div>

            {/* Coaches Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="group relative bg-white border-2 border-gray-200 rounded-3xl p-8 hover:border-emerald transition-all duration-500 hover:shadow-2xl hover:-translate-y-3 overflow-hidden"
            >
              {/* Gradient Accent */}
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-emerald via-neon-lime to-emerald rounded-t-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              {/* Icon Container */}
              <div className="relative mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald/10 to-neon-lime/5 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                  <svg className="w-10 h-10 text-emerald" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                {/* Decorative dot */}
                <div className="absolute -top-2 -right-2 w-4 h-4 bg-neon-lime rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>

              {/* Content */}
              <h3 className="text-2xl md:text-3xl font-black text-gray-900 mb-4 group-hover:text-emerald transition-colors duration-300">
                Coaches
              </h3>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                Standardize assessments and deliver data-backed feedback in minutes.
              </p>

              {/* Hover Effect Line */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-emerald to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </motion.div>

            {/* Academies Card */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="group relative bg-white border-2 border-gray-200 rounded-3xl p-8 hover:border-emerald transition-all duration-500 hover:shadow-2xl hover:-translate-y-3 overflow-hidden"
            >
              {/* Gradient Accent */}
              <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-neon-lime/60 to-neon-lime rounded-t-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              {/* Icon Container */}
              <div className="relative mb-6">
                <div className="w-20 h-20 bg-gradient-to-br from-neon-lime/10 to-emerald/5 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                  <svg className="w-10 h-10 text-emerald" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                {/* Decorative dot */}
                <div className="absolute -top-2 -right-2 w-4 h-4 bg-neon-lime rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              </div>

              {/* Content */}
              <h3 className="text-2xl md:text-3xl font-black text-gray-900 mb-4 group-hover:text-emerald transition-colors duration-300">
                Academies
              </h3>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                Make transparent, smart decisions with centralized dashboards.
              </p>

              {/* Hover Effect Line */}
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-emerald to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-24 bg-deep-charcoal overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-0 w-96 h-96 bg-emerald/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-0 w-96 h-96 bg-neon-lime/10 rounded-full blur-3xl"></div>
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <p className="text-emerald text-sm md:text-base font-bold uppercase tracking-widest mb-3">What They Say</p>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white uppercase mb-4">
              Trusted By <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald to-neon-lime">Champions</span>
            </h2>
            <p className="text-light-gray text-lg md:text-xl max-w-3xl mx-auto">
              Real feedback from parents, coaches, and academies who've transformed their game
            </p>
          </motion.div>

          {/* Testimonials Carousel */}
          <Slider
            dots={true}
            infinite={true}
            speed={1000}
            slidesToShow={1}
            slidesToScroll={1}
            autoplay={true}
            autoplaySpeed={6000}
            pauseOnHover={true}
            arrows={false}
            cssEase="ease-in-out"
            className="testimonials-slider"
          >
            {/* Parent Testimonial 1 */}
            <div className="px-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="max-w-4xl mx-auto"
              >
                <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-3xl p-8 md:p-12">
                  {/* Quote Icon */}
                  <div className="absolute top-8 left-8 opacity-20">
                    <svg className="w-16 h-16 text-emerald" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z" />
                    </svg>
                  </div>

                  {/* Content */}
                  <div className="relative z-10">
                    <p className="text-white text-lg md:text-xl lg:text-2xl leading-relaxed mb-8 italic">
                      "Thanks for sharing the assessment results and every detail in the AI report. We could see personal feedback from coaches. The back-end serve feedback detail was good. However rest looks good. Appreciate if more personal touch can be added next time."
                    </p>

                    {/* Author */}
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-emerald to-neon-lime rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-white font-bold text-lg">Parent</p>
                        <p className="text-light-gray text-sm">Student's Guardian</p>
                      </div>
                    </div>
                  </div>

                  {/* Decorative Gradient Line */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-emerald to-transparent opacity-50"></div>
                </div>
              </motion.div>
            </div>

            {/* Parent Testimonial 2 */}
            <div className="px-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="max-w-4xl mx-auto"
              >
                <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-3xl p-8 md:p-12">
                  {/* Quote Icon */}
                  <div className="absolute top-8 left-8 opacity-20">
                    <svg className="w-16 h-16 text-neon-lime" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z" />
                    </svg>
                  </div>

                  {/* Content */}
                  <div className="relative z-10">
                    <p className="text-white text-lg md:text-xl lg:text-2xl leading-relaxed mb-8 italic">
                      "Namaste, Thank you for your Report. It is very helpful to know what they learnt at camp, and it also shows where she has to improve. That's really great!"
                    </p>

                    {/* Author */}
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-neon-lime to-emerald rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-white font-bold text-lg">Parent</p>
                        <p className="text-light-gray text-sm">Student's Guardian</p>
                      </div>
                    </div>
                  </div>

                  {/* Decorative Gradient Line */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-neon-lime to-transparent opacity-50"></div>
                </div>
              </motion.div>
            </div>

            {/* Coach/Academy Testimonial */}
            <div className="px-4">
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="max-w-4xl mx-auto"
              >
                <div className="relative bg-gradient-to-br from-white/10 to-white/5 backdrop-blur-sm border border-white/20 rounded-3xl p-8 md:p-12">
                  {/* Quote Icon */}
                  <div className="absolute top-8 left-8 opacity-20">
                    <svg className="w-16 h-16 text-emerald" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z" />
                    </svg>
                  </div>

                  {/* Content */}
                  <div className="relative z-10">
                    <p className="text-white text-lg md:text-xl lg:text-2xl leading-relaxed mb-8 italic">
                      "We got insight into improving S&C drills to strengthen my students' wrist strength, after noticing they scored low on forehand airtoss compared to their backhand airtoss performance."
                    </p>

                    {/* Author */}
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-emerald via-neon-lime to-emerald rounded-full flex items-center justify-center">
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-white font-bold text-lg">Technical Director</p>
                        <p className="text-light-gray text-sm">Sports Academy</p>
                      </div>
                    </div>
                  </div>

                  {/* Decorative Gradient Line */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-emerald to-transparent opacity-50"></div>
                </div>
              </motion.div>
            </div>
          </Slider>

          {/* Custom Dots Styling */}
          <style>{`
            .testimonials-slider .slick-dots {
              bottom: -50px;
            }
            .testimonials-slider .slick-dots li button:before {
              font-size: 12px;
              color: rgba(255, 255, 255, 0.3);
              transition: all 0.3s ease;
            }
            .testimonials-slider .slick-dots li.slick-active button:before {
              color: #10b981;
              transform: scale(1.3);
            }
            .testimonials-slider .slick-dots li:hover button:before {
              color: rgba(255, 255, 255, 0.6);
            }
          `}</style>
        </div>
      </section>

      {/* Partners Section */}
      <section className="relative py-16 bg-white overflow-hidden">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-3">Trusted By</p>
            <h2 className="text-2xl md:text-3xl font-black text-gray-900 uppercase">Our Partners</h2>
          </motion.div>

          {/* Infinite Scrolling Carousel */}
          <div className="relative mb-12">
            <style>{`
              @keyframes scroll-left {
                0% {
                  transform: translateX(0);
                }
                100% {
                  transform: translateX(-50%);
                }
              }
              .scroll-container {
                animation: scroll-left 12s linear infinite;
              }
              @media (min-width: 768px) {
                .scroll-container {
                  animation: scroll-left 30s linear infinite;
                }
              }
              .scroll-container:hover {
                animation-play-state: paused;
              }
            `}</style>

            <div className="overflow-hidden">
              <div className="flex scroll-container">
                {/* Render partners twice for seamless loop */}
                {[...partners, ...partners].map((ele, index) => (
                  <div
                    key={index}
                    className="flex-shrink-0 flex items-center justify-center p-1 md:p-6 mx-1 md:mx-8 bg-gray-50 border border-gray-200 rounded-xl hover:border-gray-300 transition-all w-20 h-12 md:w-[240px] md:h-[160px]"
                  >
                    <img
                      src={ele.img}
                      alt={`Partner ${(index % partners.length) + 1}`}
                      className="max-w-full max-h-full object-contain opacity-90 hover:opacity-100 transition-all duration-300 scale-100 hover:scale-105"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>


        </div>
      </section>
    </div>
  );
}